import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ClassTypesManager from '../../components/admin/ClassTypesManager';
import PricingManager from '../../components/admin/PricingManager';
import { Users, Calendar, Activity, TrendingUp, Plus, Filter, Settings, X, Save } from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({
    classType: 'all',
    status: 'all',
    limit: 25,
    offset: 0
  });
  const [totalClasses, setTotalClasses] = useState(0);
  const [error, setError] = useState(null);
  
  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [adminSettings, setAdminSettings] = useState({
    notificationEmail: 'aileen@mandarintutorhk.com'
  });
  const [tempSettings, setTempSettings] = useState({
    notificationEmail: 'aileen@mandarintutorhk.com'
  });

  // Check admin status on mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const adminStatus = await api.admin.verifyAdmin(user.$id);
        if (!adminStatus) {
          navigate('/classes');
          return;
        }
        setIsAdmin(true);
      } catch (err) {
        console.error('Error checking admin status:', err);
        navigate('/classes');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  // Load dashboard data
  useEffect(() => {
    if (isAdmin) {
      loadDashboardData();
      loadAdminSettings();
    }
  }, [isAdmin, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load stats and classes in parallel
      const [statsResponse, classesResponse] = await Promise.all([
        api.admin.getStats(),
        api.admin.getAllClasses(filters)
      ]);

      setStats(statsResponse);
      
      if (classesResponse.success) {
        setClasses(classesResponse.classes);
        setTotalClasses(classesResponse.total);
      } else {
        throw new Error(classesResponse.message || 'Failed to load classes');
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset to first page when filtering
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/classes');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-500 bg-opacity-20 text-green-400',
      cancelled: 'bg-red-500 bg-opacity-20 text-red-400',
      completed: 'bg-gray-500 bg-opacity-20 text-gray-400',
      full: 'bg-blue-500 bg-opacity-20 text-blue-400'
    };
    
    return badges[status] || badges.active;
  };

  const handleNotificationSettingsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSettingsLoading(true);
      setSettingsError(null);
      setSettingsSuccess(false);

      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(tempSettings.notificationEmail)) {
        throw new Error('Please enter a valid email address');
      }

      // Save settings
      const response = await api.admin.updateSettings(tempSettings);
      
      if (response.success) {
        setAdminSettings(tempSettings);
        setSettingsSuccess(true);
        setTimeout(() => setSettingsSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setSettingsError(err.message || 'Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSettingsCancel = () => {
    setTempSettings(adminSettings);
    setSettingsError(null);
    setSettingsSuccess(false);
    setShowSettings(false);
  };

  const loadAdminSettings = async () => {
    try {
      const settings = await api.admin.getSettings();
      if (settings) {
        setAdminSettings(settings);
        setTempSettings(settings);
      }
    } catch (err) {
      console.error('Error loading admin settings:', err);
      // Continue with default settings if loading fails
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          {/* Left side: Title + Navigation */}
          <div className="flex items-center space-x-8">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
            
            {/* Navigation */}
            <div className="hidden sm:flex items-center space-x-6">
              <span className="text-purple-400 font-medium">Classes</span>
              <button
                onClick={() => navigate('/admin/students')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Students
              </button>
              <button
                onClick={() => navigate('/admin/tutors')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Tutors
              </button>
            </div>
          </div>
          
          {/* Right side: User controls */}
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              {user?.name}
            </span>
            <Button
              variant="outline"
              size="small"
              onClick={() => setShowSettings(true)}
              className="border-gray-600 hover:bg-gray-700 flex items-center"
            >
              <Settings size={16} className="mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={handleLogout}
              className="border-gray-600 hover:bg-gray-700"
            >
              Logout
            </Button>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        {error && (
          <div className="mb-6 p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-purple-400" />
                <div className="ml-4">
                  <p className="text-gray-400 text-sm">Total Classes</p>
                  <p className="text-2xl font-bold">{stats.totalClasses}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <div className="flex items-center">
                <Activity className="h-8 w-8 text-green-400" />
                <div className="ml-4">
                  <p className="text-gray-400 text-sm">Active Classes</p>
                  <p className="text-2xl font-bold">{stats.activeClasses}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-400" />
                <div className="ml-4">
                  <p className="text-gray-400 text-sm">Enrolled Students</p>
                  <p className="text-2xl font-bold">{stats.enrolledStudents}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-yellow-400" />
                <div className="ml-4">
                  <p className="text-gray-400 text-sm">Fill Rate</p>
                  <p className="text-2xl font-bold">{stats.fillRate}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="primary"
                onClick={() => navigate('/admin/create-class')}
                className="bg-purple-600 hover:bg-purple-700 flex items-center"
              >
                <Plus size={16} className="mr-2" />
                Create New Class
              </Button>
              
              <span className="text-gray-400 text-sm">
                Showing {classes.length} of {totalClasses} classes
              </span>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-400" />
                <select
                  value={filters.classType}
                  onChange={(e) => handleFilterChange('classType', e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-sm"
                >
                  <option value="all">All Categories</option>
                  <option value="conversation">Conversation</option>
                  <option value="business">Business</option>
                  <option value="exam_prep">Exam Prep</option>
                  <option value="academic_support">Academic Support</option>
                  <option value="language_transfer">Language Transfer</option>
                </select>
              </div>

              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Classes Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Class Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Enrollment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {classes.map((classItem) => (
                  <tr key={classItem.$id} className="hover:bg-gray-700 hover:bg-opacity-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {classItem.classTypeName || 'Unknown Class Type'}
                        </div>
                        <div className="text-sm text-gray-400">
                          ID: {classItem.$id.slice(-8)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">
                        {classItem.day} at {classItem.time}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-600 rounded-full h-2 mr-3" style={{ width: '60px' }}>
                          <div
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ width: `${classItem.fillRate}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-white">
                          {classItem.currentMembers}/{classItem.totalSpots}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(classItem.status)}`}>
                        {classItem.status.charAt(0).toUpperCase() + classItem.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(classItem.$createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => navigate(`/admin/class/${classItem.$id}`)}
                        >
                          View
                        </Button>
                        {classItem.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => navigate(`/admin/edit-class/${classItem.$id}`)}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {classes.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-400">No classes found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalClasses > filters.limit && (
          <div className="mt-6 flex justify-center">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="small"
                disabled={filters.offset === 0}
                onClick={() => handleFilterChange('offset', Math.max(0, filters.offset - filters.limit))}
              >
                Previous
              </Button>
              <span className="text-gray-400 text-sm">
                Page {Math.floor(filters.offset / filters.limit) + 1} of {Math.ceil(totalClasses / filters.limit)}
              </span>
              <Button
                variant="outline"
                size="small"
                disabled={filters.offset + filters.limit >= totalClasses}
                onClick={() => handleFilterChange('offset', filters.offset + filters.limit)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>
      
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700 rounded-t-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Settings size={20} className="mr-2" />
                  Admin Settings
                </h3>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={handleSettingsCancel}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="border-b border-gray-700">
              <div className="flex">
                <button
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'notifications'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300'
                  }`}
                  onClick={() => setActiveTab('notifications')}
                >
                  Notifications
                </button>
                <button
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'pricing'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300'
                  }`}
                  onClick={() => setActiveTab('pricing')}
                >
                  Pricing
                </button>
                <button
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'classTypes'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300'
                  }`}
                  onClick={() => setActiveTab('classTypes')}
                >
                  Class Types
                </button>
                <button
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'general'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300'
                  }`}
                  onClick={() => setActiveTab('general')}
                >
                  General
                </button>
              </div>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {activeTab === 'notifications' && (
                <>
                  {settingsError && (
                    <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded text-red-400 text-sm">
                      {settingsError}
                    </div>
                  )}
                  
                  {settingsSuccess && (
                    <div className="mb-4 p-3 bg-green-900 bg-opacity-20 border border-green-700 rounded text-green-400 text-sm">
                      Settings saved successfully!
                    </div>
                  )}

                  <form onSubmit={handleNotificationSettingsSubmit}>
                    <div className="space-y-4">
                      <Input
                        label="Notification Email Address"
                        type="email"
                        value={tempSettings.notificationEmail}
                        onChange={(e) => setTempSettings(prev => ({
                          ...prev,
                          notificationEmail: e.target.value
                        }))}
                        placeholder="admin@example.com"
                        helperText="This email will receive notifications when students join classes"
                        required
                        className="w-full"
                      />
                      
                      {/* Future notification settings */}
                      <div className="pt-4 border-t border-gray-700">
                        <h4 className="text-sm font-medium text-gray-300 mb-3">Email Preferences</h4>
                        <div className="space-y-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              className="rounded border-gray-600 text-purple-600 focus:ring-purple-500"
                              disabled
                            />
                            <span className="ml-2 text-sm text-gray-400">Daily enrollment summaries (Coming soon)</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              className="rounded border-gray-600 text-purple-600 focus:ring-purple-500"
                              disabled
                            />
                            <span className="ml-2 text-sm text-gray-400">Weekly class reports (Coming soon)</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleSettingsCancel}
                        className="bg-gray-700 hover:bg-gray-600"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={settingsLoading}
                        className="bg-purple-600 hover:bg-purple-700 flex items-center"
                      >
                        <Save size={16} className="mr-2" />
                        Save Settings
                      </Button>
                    </div>
                  </form>
                </>
              )}

              {activeTab === 'pricing' && (
                <div>
                  <PricingManager />
                </div>
              )}

              {activeTab === 'classTypes' && (
                <div>
                  <ClassTypesManager />
                </div>
              )}

              {activeTab === 'general' && (
                <div className="space-y-4">
                  <div className="text-center py-8 text-gray-400">
                    <Settings size={48} className="mx-auto mb-4 opacity-50" />
                    <p>General settings will be available soon</p>
                    <p className="text-sm mt-2">Future options: timezone, language, dashboard preferences</p>
                  </div>
                </div>
              )}

              {/* Close button for tabs that have their own save buttons or no form */}
              {(activeTab === 'classTypes' || activeTab === 'general' || activeTab === 'pricing') && (
                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSettingsCancel}
                    className="bg-gray-700 hover:bg-gray-600"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;